import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Phone, Mail, MapPin, Clock, ArrowRight, Zap,
  CheckCircle2, Send
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    company_size: '',
    users: '',
    current_provider: '',
    challenges: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/quote`, formData);
      toast.success('Quote request submitted! We\'ll be in touch within 24 hours.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        company_size: '',
        users: '',
        current_provider: '',
        challenges: '',
        message: '',
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again or call us directly.');
    }
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      value: '(602) 649-1314',
      href: 'tel:6026491314',
      description: 'Mon-Fri 7am-7pm MST',
    },
    {
      icon: Mail,
      title: 'Email',
      value: 'info@mlctech.org',
      href: 'mailto:info@mlctech.org',
      description: 'Response within 24 hours',
    },
    {
      icon: Clock,
      title: 'Support Hours',
      value: '24/7 for emergencies',
      href: null,
      description: 'Premium clients get round-the-clock support',
    },
    {
      icon: MapPin,
      title: 'Service Area',
      value: 'Nationwide',
      href: null,
      description: 'Remote support + on-site in Phoenix metro',
    },
  ];

  const reasons = [
    'Free IT assessment with no obligation',
    'Custom quote within 24 hours',
    'No high-pressure sales tactics',
    'Transparent pricing from the start',
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-slate-950 py-20" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
              <Zap className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-cyan-300">Get Your Free Quote</span>
            </div>
            
            <h1 
              className="text-4xl sm:text-5xl font-black text-white mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Let's Talk About{' '}
              <span className="text-gradient">Your IT Needs</span>
            </h1>
            
            <p className="text-xl text-slate-300">
              Fill out the form below and we'll get back to you within 24 hours 
              with a custom quote. No obligation, no pressure.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Quote Form */}
            <div className="lg:col-span-2">
              <Card className="border border-slate-200 shadow-xl" data-testid="quote-form-card">
                <CardContent className="p-8">
                  <h2 
                    className="text-2xl font-black text-slate-900 mb-6"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    Request a Quote
                  </h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Contact Information */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Your Name *</Label>
                        <Input
                          id="name"
                          placeholder="John Smith"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="h-12"
                          data-testid="contact-form-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Work Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="john@company.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="h-12"
                          data-testid="contact-form-email"
                        />
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          placeholder="(555) 123-4567"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="h-12"
                          data-testid="contact-form-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company">Company Name *</Label>
                        <Input
                          id="company"
                          placeholder="Acme Inc."
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          required
                          className="h-12"
                          data-testid="contact-form-company"
                        />
                      </div>
                    </div>

                    {/* Business Details */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company_size">Company Size</Label>
                        <Select 
                          value={formData.company_size}
                          onValueChange={(value) => setFormData({ ...formData, company_size: value })}
                        >
                          <SelectTrigger className="h-12" data-testid="contact-form-company-size">
                            <SelectValue placeholder="Select company size" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-10">1-10 employees</SelectItem>
                            <SelectItem value="11-25">11-25 employees</SelectItem>
                            <SelectItem value="26-50">26-50 employees</SelectItem>
                            <SelectItem value="51-100">51-100 employees</SelectItem>
                            <SelectItem value="100+">100+ employees</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="users">Number of Users/Devices</Label>
                        <Input
                          id="users"
                          placeholder="e.g., 25 users, 30 devices"
                          value={formData.users}
                          onChange={(e) => setFormData({ ...formData, users: e.target.value })}
                          className="h-12"
                          data-testid="contact-form-users"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="current_provider">Current IT Provider (if any)</Label>
                      <Input
                        id="current_provider"
                        placeholder="e.g., In-house, Other MSP, None"
                        value={formData.current_provider}
                        onChange={(e) => setFormData({ ...formData, current_provider: e.target.value })}
                        className="h-12"
                        data-testid="contact-form-provider"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="challenges">Current IT Challenges</Label>
                      <Select 
                        value={formData.challenges}
                        onValueChange={(value) => setFormData({ ...formData, challenges: value })}
                      >
                        <SelectTrigger className="h-12" data-testid="contact-form-challenges">
                          <SelectValue placeholder="What's your biggest IT challenge?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="slow-support">Slow or unresponsive IT support</SelectItem>
                          <SelectItem value="security">Security concerns</SelectItem>
                          <SelectItem value="costs">High IT costs</SelectItem>
                          <SelectItem value="downtime">Frequent downtime</SelectItem>
                          <SelectItem value="no-it">No current IT support</SelectItem>
                          <SelectItem value="growth">Scaling for growth</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Additional Details</Label>
                      <Textarea
                        id="message"
                        placeholder="Tell us more about your IT needs or any specific questions..."
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={4}
                        data-testid="contact-form-message"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-500/20"
                      disabled={isSubmitting}
                      data-testid="contact-form-submit"
                    >
                      {isSubmitting ? (
                        'Submitting...'
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Submit Quote Request
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-slate-500 text-center">
                      By submitting, you agree to our privacy policy. We'll never share your information.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info Sidebar */}
            <div className="space-y-6">
              {/* Contact Cards */}
              {contactInfo.map((info) => (
                <Card 
                  key={info.title} 
                  className="border border-slate-200"
                  data-testid={`contact-info-${info.title.toLowerCase()}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{info.title}</h3>
                        {info.href ? (
                          <a 
                            href={info.href} 
                            className="text-blue-600 hover:text-blue-700 font-medium"
                          >
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-slate-900 font-medium">{info.value}</p>
                        )}
                        <p className="text-sm text-slate-500">{info.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Why Contact Us */}
              <Card className="border border-blue-200 bg-blue-50" data-testid="why-contact-card">
                <CardContent className="p-6">
                  <h3 
                    className="font-bold text-slate-900 mb-4"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    Why Request a Quote?
                  </h3>
                  <ul className="space-y-3">
                    {reasons.map((reason) => (
                      <li key={reason} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700">{reason}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Quick Call CTA */}
              <Card className="border border-slate-200 bg-slate-900" data-testid="quick-call-card">
                <CardContent className="p-6 text-center">
                  <h3 
                    className="font-bold text-white mb-2"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    Prefer to Talk?
                  </h3>
                  <p className="text-slate-400 text-sm mb-4">
                    Call us directly for immediate assistance.
                  </p>
                  <a href="tel:6026491314">
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                      data-testid="quick-call-btn"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      (602) 649-1314
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
